import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * The window's width. '2xl' (max-w-6xl, its body up to 70vh) is for a window that holds a
   * wide table, Link an opening's openings (16 Oct 2026); every other size is as it was.
   * '2xl' alone stands near the top of the screen rather than in its middle (48px down from a
   * laptop's width, as the approved mock-up has it), so a search that filters its table makes
   * the window shorter without moving the box being typed in.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  headerContent?: React.ReactNode;
  /**
   * A line between the header and the body, outside the body's scrolling part: what scrolls
   * itself into view in the body cannot push it out of sight (the hiring window's "<person> is
   * deployed. Now finish …", 22 Oct 2026). Without it the window is as it always was.
   */
  belowHeader?: React.ReactNode;
}

/**
 * How many Modals are open now. The page behind stays locked until the LAST one closes: a
 * window opened over another (Edit Store over Raise hiring) must not unlock the page when
 * it closes while the one beneath is still open - nor may a Modal that is mounted closed.
 */
let openModals = 0;

export default function Modal({ isOpen, onClose, title, children, footer, size = 'md', headerContent, belowHeader }: ModalProps) {
  useEffect(() => {
    if (!isOpen) return undefined;
    openModals += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      openModals = Math.max(0, openModals - 1);
      if (openModals === 0) document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-5xl',
    '2xl': 'max-w-6xl',
  } as const;
  // How tall the body may grow before it scrolls: 60vh, as always, but for the widest window,
  // whose table gets a little more of a laptop's height (70vh).
  const bodyHeight = size === '2xl' ? 'max-h-[70vh]' : 'max-h-[60vh]';
  // Where the window stands: in the middle, as always, but for the widest window, whose height
  // follows its table's search - kept at the top, so its search box stays where it is.
  const place = size === '2xl' ? 'items-start sm:pt-12' : 'items-center';

  // Portal to <body> so the overlay covers the FULL viewport (incl. the sticky
  // top nav). Without this, a containing-block ancestor traps `fixed` and the
  // backdrop starts below the header. z-[100] sits above the header (z-30).
  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className={`flex min-h-screen justify-center p-4 ${place}`}>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`relative w-full rounded-lg bg-white shadow-2xl ${sizes[size]}`}>
          {/* Header */}
          {headerContent ? (
            headerContent
          ) : (
            <div className="flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {belowHeader}

          {/* Content */}
          <div className={`${bodyHeight} overflow-y-auto px-6 py-4`}>{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
